import type { AfterContentInit, OnDestroy } from '@angular/core';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ContentChildren,
  HostBinding,
  Input,
  QueryList,
} from '@angular/core';
import {
  LinkColor,
  LinkHoverColor,
  LinkWeight,
  QueryParams,
} from '@freelancer/ui/link';
import { FontColor, TextSize, TextTransform } from '@freelancer/ui/text';
import type { Subscription } from 'rxjs';
import { startWith } from 'rxjs/operators';

@Component({
  selector: `fl-breadcrumbs-item`,
  template: `
    <fl-link
      [color]="LinkColor.INHERIT"
      [hoverColor]="LinkHoverColor.INHERIT"
      [itemprop]="'item'"
      [itemtype]="'https://schema.org/Thing'"
      [link]="link"
      [queryParams]="queryParams"
      [size]="size"
      [weight]="weight"
      [disabled]="disabled"
    >
      <span itemprop="name"><ng-content></ng-content></span>
    </fl-link>
    <meta
      itemprop="position"
      [content]="position"
    />
  `,
  styleUrls: ['./breadcrumbs-item.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class BreadcrumbsItemComponent {
  LinkColor = LinkColor;
  LinkHoverColor = LinkHoverColor;
  FontColor = FontColor;
  TextTransform = TextTransform;

  @Input() link: string;
  @Input() position: number;
  @Input() queryParams?: QueryParams;
  @Input() size = TextSize.XXXSMALL;
  @Input() weight = LinkWeight.BOLD;
  @Input() disabled = false;

  @HostBinding('attr.data-color')
  @Input()
  color = FontColor.FOREGROUND;

  @HostBinding('attr.data-text-transform')
  @Input()
  textTransform = TextTransform.UPPERCASE;

  @HostBinding('attr.data-mobile-show-breadcrumb')
  @Input()
  mobileShowBreadcrumb?: boolean;

  @HostBinding('attr.itemprop') itemprop = 'itemListElement';
  @HostBinding('attr.itemscope') itemscope = '';
  @HostBinding('attr.itemtype') itemtype = 'https://schema.org/ListItem';
  @HostBinding('attr.role') role = 'listitem';

  constructor(public changeDetector: ChangeDetectorRef) {}
}

@Component({
  selector: 'fl-breadcrumbs',
  template: `
    <div
      role="list"
      class="Breadcrumbs"
      itemscope
      itemtype="https://schema.org/BreadcrumbList"
    >
      <ng-content></ng-content>
    </div>
  `,
  styleUrls: ['./breadcrumbs.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class BreadcrumbsComponent implements AfterContentInit, OnDestroy {
  FontColor = FontColor;
  TextTransform = TextTransform;

  @ContentChildren(BreadcrumbsItemComponent)
  breadcrumbsItems: QueryList<BreadcrumbsItemComponent>;

  private itemsSubscription?: Subscription;

  @Input() color = FontColor.FOREGROUND;
  @Input() textTransform = TextTransform.UPPERCASE;

  ngAfterContentInit(): void {
    this.itemsSubscription = this.breadcrumbsItems.changes
      .pipe(startWith(this.breadcrumbsItems.toArray()))
      .subscribe(() => {
        this.breadcrumbsItems.forEach((component, index) => {
          component.position = index + 1;
          // T255421: Using detectChanges() since markForCheck() leads to an undefined position in the ssr page.
          component.color = this.color;
          component.textTransform = this.textTransform;
          component.changeDetector.detectChanges();
        });
      });
  }

  ngOnDestroy(): void {
    if (this.itemsSubscription) {
      this.itemsSubscription.unsubscribe();
    }
  }
}
